/**
 * Simplified Firebase Authentication Service
 * Uses Firebase Client SDK directly with Firestore Security Rules
 * No backend OTP required - direct Google authentication
 * Updated: 2025-01-13 09:45 - Fixed permission error handling
 */

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import firestoreOTPService from './firestoreOTPService';
import cloudFunctionEmailService from './cloudFunctionEmailService';

class SimplifiedAuthService {
  constructor() {
    this._redirectPromise = null; // Promise cache to prevent multiple simultaneous redirect handlers
    console.log('🔄 SimplifiedAuthService v2.0 - Updated with permission error handling');
    this.provider = new GoogleAuthProvider();
    this.currentUser = null;
    this.userPermissions = null;
    this.organizationId = 'drishti_lifesaving_org_id';

    // Configure Google Auth Provider
    this.provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    // Clean up stale redirect flags on startup (older than 5 minutes)
    this.cleanupStaleRedirectFlags();
  }
  
  /**
   * Clean up stale redirect flags that might be left from previous sessions
   */
  cleanupStaleRedirectFlags() {
    try {
      const redirectTime = sessionStorage.getItem('oauth_redirect_time');
      const redirectStarted = sessionStorage.getItem('oauth_redirect_started');
      
      if (redirectTime) {
        const timeSinceRedirect = Date.now() - parseInt(redirectTime);
        // If redirect was initiated more than 2 minutes ago, clear it
        // (More aggressive cleanup to prevent loops)
        if (timeSinceRedirect > 120000) {
          console.log('🧹 Cleaning up stale redirect flag (', Math.round(timeSinceRedirect / 1000), 'seconds old)');
          sessionStorage.removeItem('oauth_redirect_started');
          sessionStorage.removeItem('oauth_redirect_time');
        } else if (redirectStarted === 'true') {
          // Check if we're in a potential redirect loop
          // If redirect flag exists but no OAuth params and no user, it might be a loop
          const urlParams = new URLSearchParams(window.location.search);
          const urlHash = window.location.hash;
          const hasOAuthParams = urlParams.has('code') || 
                                 urlParams.has('state') ||
                                 urlHash.includes('access_token') ||
                                 urlHash.includes('id_token');
          
          if (!hasOAuthParams && !auth.currentUser && timeSinceRedirect > 10000) {
            // Redirect flag exists for more than 10 seconds but no OAuth params and no user
            // This suggests a redirect loop or failed redirect
            console.log('⚠️ Potential redirect loop detected - clearing redirect flags');
            console.log('   Time since redirect:', Math.round(timeSinceRedirect / 1000), 'seconds');
            console.log('   Has OAuth params:', hasOAuthParams);
            console.log('   Has authenticated user:', !!auth.currentUser);
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
          }
        }
      }
    } catch (error) {
      // Ignore errors in cleanup
      console.warn('⚠️ Error in cleanupStaleRedirectFlags:', error);
    }
  }

  getAuthDomainErrorMessage(error) {
    const code = error?.code || '';
    if (code !== 'auth/invalid-continue-uri' && code !== 'auth/unauthorized-domain') {
      return null;
    }

    const origin = (typeof window !== 'undefined' && window.location?.origin)
      ? window.location.origin
      : 'this site';
    
    const hostname = (typeof window !== 'undefined' && window.location?.hostname)
      ? window.location.hostname
      : 'localhost';
    
    const projectId = auth.app.options.projectId || 'dwpl-atlas';
    const consoleUrl = `https://console.firebase.google.com/project/${projectId}/authentication/settings`;

    // Check if we're on localhost with a port
    const isLocalhostWithPort = hostname === 'localhost' && origin.includes(':');
    const portNumber = isLocalhostWithPort ? origin.split(':').pop() : null;
    
    let fixInstructions = `🔧 How to fix:
1. Go to Firebase Console: ${consoleUrl}
2. Click "Authentication" → "Settings" → "Authorized domains"
3. Click "Add domain"`;
    
    if (isLocalhostWithPort) {
      fixInstructions += `
4. ⚠️ IMPORTANT: This error requires fixing in Google Cloud Console, not Firebase Console!
   
   Firebase Console already has "localhost" authorized ✅
   But Google Cloud Console OAuth settings need the redirect URI:
   
   a. Go to: https://console.cloud.google.com/apis/credentials?project=${projectId}
   b. Find "OAuth 2.0 Client IDs" section
   c. Click on your Web client (the one used by Firebase)
   d. Under "Authorized redirect URIs", click "ADD URI"
   e. Add these URIs:
      - ${origin}/__/auth/handler
      - ${origin}
   f. Click "SAVE"
   g. Wait 1-2 minutes for changes to propagate
   
   Alternative workaround:
   - Try accessing your app via: http://127.0.0.1:${portNumber}
   - Or use Firebase Auth Emulator (if available)`;
    } else {
      fixInstructions += `
4. Add: ${hostname} (without port)
   OR add: ${origin} (with port if applicable)
5. Click "Done"`;
    }
    
    fixInstructions += `
6. Wait 1-2 minutes for changes to propagate
7. Refresh this page and try again

💡 Note: The "invalid-continue-uri" error with popup usually means the redirect URI 
   needs to be added in Google Cloud Console OAuth settings, not just Firebase Console.`;
    
    return `Sign-in blocked: ${origin} is not an authorized domain for Firebase Auth.

${fixInstructions}`;
  }

  /**
   * Convert email to document ID format
   * Example: abbas.merchant@drishti.works -> abbas_merchant_at_drishti_works
   */
  emailToDocId(email) {
    if (!email) return null;
    return email.replace(/[.@]/g, (match) => {
      if (match === '.') return '_';
      if (match === '@') return '_at_';
      return match;
    });
  }

  /**
   * Sign in with Google (requires OTP verification)
   * Uses popup method for better reliability, especially on localhost
   */
  async signInWithGoogle() {
    try {
      console.log('🔄 Simplified Auth: Starting Google sign-in...');

      // Set persistence FIRST (important for popup flow)
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('✅ Auth persistence set to LOCAL');
      } catch (persistenceError) {
        console.warn('⚠️ Could not set persistence:', persistenceError.message);
        // Continue anyway - persistence might already be set
      }

      // Configure provider with account selection prompt
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',  // Force account picker
        login_hint: ''  // Don't auto-fill
      });
      provider.addScope('email');
      provider.addScope('profile');

      let result;
      
      // Check if we have a stale redirect flag first - clear it if it's old
      const existingRedirectTime = sessionStorage.getItem('oauth_redirect_time');
      if (existingRedirectTime) {
        const timeSinceRedirect = Date.now() - parseInt(existingRedirectTime);
        if (timeSinceRedirect > 120000) { // 2 minutes
          console.log('🧹 Clearing stale redirect flag before new sign-in attempt');
          sessionStorage.removeItem('oauth_redirect_started');
          sessionStorage.removeItem('oauth_redirect_time');
        }
      }
      
      // Try popup first (works better on localhost)
      // For localhost, popup should work even if redirect has issues
      try {
        console.log('🔑 Opening Google sign-in popup...');
        console.log('📍 Current URL:', window.location.href);
        console.log('📍 Current origin:', window.location.origin);
        console.log('📍 Current hostname:', window.location.hostname);
        
        result = await signInWithPopup(auth, provider);
        console.log('✅ Google sign-in successful (popup):', result.user.email);
      } catch (popupError) {
        console.error('❌ Popup sign-in failed:', {
          code: popupError.code,
          message: popupError.message,
          name: popupError.name,
          stack: popupError.stack
        });
        console.log('⚠️ Popup failed:', popupError.code, popupError.message);
        
        // For invalid-continue-uri on localhost, this is unusual
        // Popup shouldn't need redirect URI configuration
        if (popupError.code === 'auth/invalid-continue-uri') {
          console.error('⚠️ UNUSUAL: invalid-continue-uri error with popup');
          console.error('   This usually indicates a Google Cloud Console OAuth configuration issue');
          console.error('   Popup authentication should work with just "localhost" authorized in Firebase');
          console.error('');
          console.error('🔧 FIX REQUIRED: Add redirect URI in Google Cloud Console');
          console.error('   1. Go to: https://console.cloud.google.com/apis/credentials?project=' + auth.app.options.projectId);
          console.error('   2. Find your OAuth 2.0 Client ID (Web client)');
          console.error('   3. Click to edit it');
          console.error('   4. Under "Authorized redirect URIs", add:');
          console.error('      - http://localhost:3000/__/auth/handler');
          console.error('      - http://localhost:3000');
          console.error('   5. Save and wait 1-2 minutes');
          console.error('');
          console.error('   Alternative: Try accessing via http://127.0.0.1:3000 instead');
        }
        
        // Check if it's a popup-blocked error
        if (popupError.code === 'auth/popup-blocked') {
          console.log('🚫 Popup blocked by browser');
          console.log('💡 Please allow pop-ups for this site and try again');
          
          // Try redirect as fallback
          console.log('🔄 Falling back to redirect method...');
          try {
            sessionStorage.setItem('oauth_redirect_started', 'true');
            sessionStorage.setItem('oauth_redirect_time', Date.now().toString());
            await signInWithRedirect(auth, provider);
            return {
              success: true,
              redirecting: true,
              message: 'Popup blocked. Redirecting to Google sign-in...',
              popupBlocked: true
            };
          } catch (redirectError) {
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
            const domainMessage = this.getAuthDomainErrorMessage(redirectError);
            console.error('❌ Redirect fallback also failed:', redirectError);
            return {
              success: false,
              error: `Popup blocked and redirect failed: ${domainMessage || redirectError.message}`,
              popupBlocked: true
            };
          }
        } else if (popupError.code === 'auth/popup-closed-by-user') {
          // User closed the popup - don't treat as error
          console.log('ℹ️ User closed the sign-in popup');
          return {
            success: false,
            error: 'Sign-in cancelled',
            cancelled: true
          };
        } else if (popupError.code === 'auth/unauthorized-domain') {
          // Domain not authorized in Firebase Console
          console.error('❌ Domain not authorized in Firebase Console!');
          return {
            success: false,
            error: 'Configuration error: This domain is not authorized. Please add it to Firebase Console → Authentication → Settings → Authorized domains.',
            unauthorizedDomain: true
          };
        } else {
          // Other popup error - check if it's a network/auth error that we should handle differently
          console.log('⚠️ Popup error, checking error type:', popupError.code);
          
          // Don't fall back to redirect for certain errors that indicate configuration issues
          if (popupError.code === 'auth/unauthorized-domain' || 
              popupError.code === 'auth/invalid-continue-uri' ||
              popupError.code === 'auth/operation-not-allowed') {
            const domainMessage = this.getAuthDomainErrorMessage(popupError);
            console.error('❌ Configuration error - redirect will also fail:', popupError.code);
            console.error('📋 Detailed error message:', domainMessage);
            
            // Log helpful information
            const projectId = auth.app.options.projectId;
            const currentOrigin = window.location.origin;
            const currentHostname = window.location.hostname;
            console.error('🔍 Current configuration:');
            console.error('   Project ID:', projectId);
            console.error('   Current origin:', currentOrigin);
            console.error('   Current hostname:', currentHostname);
            console.error('   Firebase Console URL:', `https://console.firebase.google.com/project/${projectId}/authentication/settings`);
            
            return {
              success: false,
              error: domainMessage || popupError.message || 'Authentication configuration error',
              configurationError: true,
              projectId: projectId,
              currentOrigin: currentOrigin,
              consoleUrl: `https://console.firebase.google.com/project/${projectId}/authentication/settings`
            };
          }
          
          // For other errors, try redirect as fallback
          console.log('⚠️ Popup error, trying redirect as fallback:', popupError.message);
          try {
            // Clear any existing redirect flags first
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
            
            // Set new redirect flags
            sessionStorage.setItem('oauth_redirect_started', 'true');
            sessionStorage.setItem('oauth_redirect_time', Date.now().toString());
            
            console.log('🔄 Initiating redirect to Google sign-in...');
            console.log('📍 Current URL before redirect:', window.location.href);
            
            await signInWithRedirect(auth, provider);
            
            // This line should not execute if redirect works (page will reload)
            return {
              success: true,
              redirecting: true,
              message: 'Redirecting to Google sign-in...'
            };
          } catch (redirectError) {
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
            const domainMessage = this.getAuthDomainErrorMessage(redirectError);
            console.error('❌ Redirect fallback failed:', {
              code: redirectError.code,
              message: redirectError.message,
              domainMessage
            });
            return {
              success: false,
              error: domainMessage || redirectError.message || popupError.message
            };
          }
        }
      }

      const user = result.user;

      // Try to create or update user in Firestore, but don't fail if permission denied
      const userResult = await this.createOrUpdateUser(user);
      if (userResult && userResult.success === false) {
        console.error('❌ Google Sign-In failed:', userResult.error);
        return { success: false, error: userResult.error };
      } else {
        console.log('✅ User created/updated successfully');
      }

      // Check if user is whitelisted (try both UID and email-based lookup)
      const { isWhitelisted } = await this.checkUserWhitelist(user);

      if (!isWhitelisted) {
        console.log('❌ User not whitelisted:', user.email);
        return {
          success: false,
          error: 'User not authorized to access this system',
          user: {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL
          }
        };
      }

      // User is whitelisted - return success but NO permissions yet
      // OTP verification will be required before permissions are loaded
      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        },
        permissions: null // No permissions until OTP is verified
      };
    } catch (error) {
      const domainMessage = this.getAuthDomainErrorMessage(error);
      console.error('❌ Google sign-in error:', error);
      return {
        success: false,
        error: domainMessage || error.message
      };
    }
  }

  /**
   * Handle redirect result after Google sign-in
   * This should only be called once on page load to check if user is returning from redirect
   * Uses promise caching to prevent multiple simultaneous calls
   */
  async handleRedirectResult() {
    // Clean up stale redirect flags before processing
    this.cleanupStaleRedirectFlags();
    
    // If already processing, return the existing promise
    if (this._redirectPromise) {
      console.log('⏳ Redirect result already being processed, waiting for result...');
      return this._redirectPromise;
    }
    
    // Create a new promise for this redirect handling
    this._redirectPromise = (async () => {
      try {
        // First, try getRedirectResult (this only works once per redirect)
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          console.log('✅ Google sign-in successful (redirect):', result.user.email);

          const user = result.user;

          // Try to create or update user in Firestore, but don't fail if permission denied
          const userResult = await this.createOrUpdateUser(user);
          if (userResult && userResult.success === false) {
            console.error('❌ Google Sign-In (redirect) failed:', userResult.error);
            return { success: false, error: userResult.error };
          } else {
            console.log('✅ User created/updated successfully (redirect)');
          }

          // Check if user is whitelisted (try both UID and email-based lookup)
          const { isWhitelisted } = await this.checkUserWhitelist(user);

          if (!isWhitelisted) {
            console.log('❌ User not whitelisted:', user.email);
            return {
              success: false,
              error: 'User not authorized to access this system',
              user: {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL
              }
            };
          }

          // User is whitelisted - return success but NO permissions yet
          return {
            success: true,
            user: {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL
            },
            permissions: null // No permissions until OTP is verified
          };
        }
        
        // Fallback: Check if user is already authenticated
        // This handles cases where getRedirectResult was already called or page reloaded
        // Check if we came from a redirect first
        const redirectTime = sessionStorage.getItem('oauth_redirect_time');
        const cameFromRedirect = sessionStorage.getItem('oauth_redirect_started') === 'true';
        const urlParams = new URLSearchParams(window.location.search);
        const urlHash = window.location.hash;
        
        // Check for OAuth params more thoroughly
        const hasOAuthParams = urlParams.has('code') || 
                               urlParams.has('state') ||
                               urlHash.includes('access_token') ||
                               urlHash.includes('id_token') ||
                               urlHash.includes('authuser') ||
                               urlHash.includes('prompt');
        
        // Log current state for debugging
        console.log('🔍 Redirect detection state:', {
          cameFromRedirect,
          redirectTime: redirectTime ? new Date(parseInt(redirectTime)).toISOString() : null,
          currentURL: window.location.href,
          hasOAuthParams,
          urlSearch: window.location.search,
          urlHash: urlHash.substring(0, 100), // First 100 chars of hash
          currentUser: auth.currentUser ? auth.currentUser.email : null
        });
        
        // Validate redirect flag is not stale
        let isValidRedirect = false;
        if (cameFromRedirect && redirectTime) {
          const timeSinceRedirect = Date.now() - parseInt(redirectTime);
          // Redirect must be less than 2 minutes old to be valid
          if (timeSinceRedirect < 120000) {
            isValidRedirect = true;
            console.log('✅ Valid redirect flag detected (', Math.round(timeSinceRedirect / 1000), 'seconds old)');
          } else {
            console.log('⚠️ Stale redirect flag detected (', Math.round(timeSinceRedirect / 1000), 'seconds old) - clearing');
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
          }
        }
        
        // If we have OAuth params OR a valid redirect flag, wait for auth state
        // But only if user is not already authenticated
        if ((isValidRedirect || hasOAuthParams) && !auth.currentUser) {
          console.log('🔍 Detected OAuth redirect return, waiting for auth state...');
          console.log('   Redirect flag:', isValidRedirect ? '✅ Valid' : '❌ Invalid');
          console.log('   OAuth params:', hasOAuthParams ? '✅ Present' : '❌ Missing');
          
          // Use onAuthStateChanged to wait for auth state (more reliable than polling)
          const user = await new Promise((resolve) => {
            let resolved = false;
            let unsubscribe = null;
            
            const timeout = setTimeout(() => {
              if (!resolved) {
                resolved = true;
                if (unsubscribe) unsubscribe();
                console.log('⏱️ Timeout waiting for auth state after redirect (5 seconds)');
                console.log('   This might indicate:');
                console.log('   1. OAuth redirect was cancelled by user');
                console.log('   2. Redirect URI mismatch in Firebase Console');
                console.log('   3. Network issue preventing auth completion');
                resolve(null);
              }
            }, 5000); // 5 second timeout
            
            unsubscribe = onAuthStateChanged(auth, (user) => {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                unsubscribe();
                console.log('🔔 Auth state changed:', user ? user.email : 'null');
                resolve(user);
              }
            }, (error) => {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                unsubscribe();
                console.error('❌ Auth state error:', error);
                resolve(null);
              }
            });
            
            // Also check current user immediately (might already be set)
            if (auth.currentUser) {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                unsubscribe();
                console.log('✅ User already authenticated:', auth.currentUser.email);
                resolve(auth.currentUser);
              }
            }
          });
          
          console.log('🔍 Auth state check result:', {
            user: user ? user.email : 'null',
            cameFromRedirect: isValidRedirect,
            hasOAuthParams
          });
          
          if (user) {
            console.log('✅ User authenticated after redirect, processing...');
            
            // Clear the flags
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
            
            // Try to create or update user in Firestore
            const userResult = await this.createOrUpdateUser(user);
            if (userResult && userResult.success === false) {
              console.error('❌ User creation/update failed:', userResult.error);
              return { success: false, error: userResult.error };
            }
            
            // Check if user is whitelisted
            const { isWhitelisted } = await this.checkUserWhitelist(user);
            
            if (!isWhitelisted) {
              console.log('❌ User not whitelisted:', user.email);
              return {
                success: false,
                error: 'User not authorized to access this system',
                user: {
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName,
                  photoURL: user.photoURL
                }
              };
            }
            
            // User is whitelisted - return success
            return {
              success: true,
              user: {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL
              },
              permissions: null
            };
          } else {
            // Redirect was detected but auth didn't complete
            console.log('⚠️ Redirect detected but user not authenticated after waiting.');
            console.log('   Possible causes:');
            console.log('   1. User cancelled OAuth flow on Google page');
            console.log('   2. Redirect URI not configured in Firebase Console');
            console.log('   3. Network issue preventing auth completion');
            console.log('   Clearing redirect flags and allowing normal login flow...');
            sessionStorage.removeItem('oauth_redirect_started');
            sessionStorage.removeItem('oauth_redirect_time');
          }
        } else if (cameFromRedirect && !isValidRedirect) {
          // Stale redirect flag - clear it
          console.log('🧹 Clearing stale redirect flag');
          sessionStorage.removeItem('oauth_redirect_started');
          sessionStorage.removeItem('oauth_redirect_time');
        } else if (cameFromRedirect && !hasOAuthParams && auth.currentUser === null) {
          // We have a redirect flag but no OAuth params and no user
          // This means the redirect didn't complete properly
          console.log('⚠️ Redirect flag present but no OAuth params and no authenticated user');
          console.log('   This suggests the redirect flow was interrupted or failed');
          console.log('   Clearing redirect flags to allow fresh login attempt...');
          sessionStorage.removeItem('oauth_redirect_started');
          sessionStorage.removeItem('oauth_redirect_time');
        } else {
          // Check if user is authenticated but not from redirect
          const currentUser = auth.currentUser;
          if (currentUser) {
            console.log('ℹ️ User is authenticated but not from a recent redirect (existing session)');
          }
        }
        
        // No redirect result and no authenticated user from redirect - this is normal if user didn't come from a redirect
        return { success: false, error: 'No redirect result' };
      } catch (error) {
        const domainMessage = this.getAuthDomainErrorMessage(error);
        console.error('❌ Redirect result error:', error);
        return {
          success: false,
          error: domainMessage || error.message
        };
      } finally {
        // Clear the promise after processing (allow future calls)
        this._redirectPromise = null;
      }
    })();
    
    return this._redirectPromise;
  }

  /**
   * Create or update user in Firestore
   */
  async createOrUpdateUser(user) {
    try {
      const uidRef = doc(db, 'users', user.uid);
      const uidSnap = await getDoc(uidRef);

      const emailDocId = this.emailToDocId(user.email);
      const emailRef = doc(db, 'users', emailDocId);
      const emailSnap = await getDoc(emailRef);

      // New: Query by email field to find user regardless of document ID
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where("email", "==", user.email));
      const querySnapshot = await getDocs(q);

      // Check if user exists by either UID, email-based ID, or email field query
      if (!uidSnap.exists() && !emailSnap.exists() && querySnapshot.empty) {
        console.error(`Login attempt failed: User with email ${user.email} not found in 'users' collection by UID, email ID, or email field query.`);
        return { success: false, error: 'Access denied. Contact Admin or support.' };
      }

      // Find the definitive user document
      let existingUserDoc;
      if (uidSnap.exists()) {
        existingUserDoc = uidSnap;
      } else if (emailSnap.exists()) {
        existingUserDoc = emailSnap;
      } else {
        // If not found by UID or emailDocId, it must be found by the email field query
        // We need to get the document from the querySnapshot
        if (querySnapshot.empty) {
          console.error(`Login attempt failed: User with email ${user.email} not found in 'users' collection by email field query.`);
          return { success: false, error: 'Access denied. Contact Admin or support.' };
        }
        existingUserDoc = querySnapshot.docs[0];
      }

      // Use email-normalized format for document ID (consistent with backend)
      const normalizedEmailDocId = this.emailToDocId(user.email);
      const userRefForUpdate = doc(db, 'users', normalizedEmailDocId);
      
      const userData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastLogin: serverTimestamp(),
        authMethod: 'google_direct',
        uid: normalizedEmailDocId,  // Use normalized email format as uid
        originalUid: user.uid,  // Store original Firebase UID for reference
        emailDocId: normalizedEmailDocId
      };

      // We should check which snapshot existed to get the login count and creation date.
      const existingData = existingUserDoc.data();
      
      await setDoc(userRefForUpdate, {
        ...userData,
        createdAt: existingData.createdAt || serverTimestamp(), // Preserve original creation date
        loginCount: (existingData.loginCount || 0) + 1
      }, { merge: true }); // Use merge to avoid overwriting fields like roles

      console.log(`✅ User updated/migrated in Firestore with email-normalized ID: ${normalizedEmailDocId}`);

      // Optional: if the old email-based doc exists and is different from the uid-based doc, we could delete it.
      // For now, we will not delete it to be safe. It can be a separate cleanup task.

      return { success: true };
    } catch (error) {
      console.error('❌ User creation/update error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user is whitelisted (exists in the organization's members list)
   */
  async checkUserWhitelist(user) {
    try {
      console.log('🔍 Checking whitelist for:', user.email);

      // Primary check: Is the user a member of the organization by UID?
      const memberRef = doc(db, 'organizations', this.organizationId, 'members', user.uid);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        console.log('✅ User found in organization members by UID:', user.uid);
        return { isWhitelisted: true };
      }

      // Fallback for older users: Check by email-based document ID.
      const emailDocId = this.emailToDocId(user.email);
      const emailMemberRef = doc(db, 'organizations', this.organizationId, 'members', emailDocId);
      const emailMemberSnap = await getDoc(emailMemberRef);

      if (emailMemberSnap.exists()) {
        console.log('✅ User found in organization members by email:', user.email);
        return { isWhitelisted: true };
      }

      // Allow users from company domains
      const allowedDomains = ['@drishti.works', '@drishtitech.com', '@drishtigroup.com'];
      if (user.email && allowedDomains.some(domain => user.email.endsWith(domain))) {
        console.log('✅ User whitelisted by domain:', user.email);
        return { isWhitelisted: true };
      }

      console.log('❌ User not found in organization members:', user.email);
      return { isWhitelisted: false };
    } catch (error) {
      console.error('❌ Error checking whitelist:', error);
      const allowedDomains = ['@drishti.works', '@drishtitech.com', '@drishtigroup.com'];
      if (user.email && allowedDomains.some(domain => user.email.endsWith(domain))) {
        console.log('✅ User whitelisted by domain (error fallback):', user.email);
        return { isWhitelisted: true };
      }
      return { isWhitelisted: false };
    }
  }

  /**
   * Load user permissions from organization membership
   */
  async loadUserPermissions(userId, userEmail = null) {
    try {
      console.log('🔄 Loading user permissions for:', userId);

      // Use email-normalized format as primary method for organization membership lookup
      let memberSnap = null;
      let memberRef = null;

      if (userEmail) {
        // Primary: Try email-based document ID (new standard)
        const emailDocId = this.emailToDocId(userEmail);
        memberRef = doc(db, 'organizations', this.organizationId, 'members', emailDocId);
        memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
          console.log('✅ Found user in organization by email-based ID');
        }
      }

      // Fallback: Try Firebase UID for backward compatibility (if email-based lookup failed)
      if (!memberSnap || !memberSnap.exists()) {
        console.log('⚠️ User not found with email-based ID, trying UID-based lookup for backward compatibility');
        memberRef = doc(db, 'organizations', this.organizationId, 'members', userId);
        memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
          console.log('✅ Found user in organization by UID (backward compatibility)');
        }
      }

      if (!memberSnap || !memberSnap.exists()) {
        console.warn('⚠️ User not found in organization, attempting to add them...');

        // Try to add user to organization with guest role
        const addResult = await this.addUserToOrganization(userId, userEmail);
        if (addResult.success) {
          console.log('✅ User added to organization, retrying permission load...');
          // Retry loading permissions (will use email-based ID now)
          return await this.loadUserPermissions(userId, userEmail);
        } else {
          console.warn('⚠️ Failed to add user to organization, using default permissions');
          this.userPermissions = this.getDefaultPermissions();
          return;
        }
      }

      const userRole = memberSnap.data().role;
      console.log('👤 User role:', userRole);

      // Load role permissions
      const roleRef = doc(db, 'roles', userRole);
      const roleSnap = await getDoc(roleRef);

      if (roleSnap.exists()) {
        this.userPermissions = roleSnap.data().permissions;
        console.log('✅ Permissions loaded:', this.userPermissions);
      } else {
        console.warn('⚠️ Role not found, using default permissions');
        this.userPermissions = this.getDefaultPermissions();
      }
    } catch (error) {
      console.error('❌ Permission loading error:', error);
      this.userPermissions = this.getDefaultPermissions();
    }
  }

  /**
   * Add user to organization with guest role (default)
   * Uses email-normalized format for member document ID (e.g., )
   */
  async addUserToOrganization(userId, userEmail = null) {
    try {
      console.log('🔄 Adding user to organization:', userId);

      // This is the user object from the initial auth, not a live object.
      // We rely on the passed-in arguments.
      if (!userId || !userEmail) {
        return { success: false, error: 'User ID or Email not provided to addUserToOrganization' };
      }

      const companyDomains = ['@drishti.works', '@drishtitech.com', '@drishtigroup.com'];
      const isCompanyUser = companyDomains.some(domain => userEmail.endsWith(domain));
      const assignedRole = isCompanyUser ? 'admin' : 'guest';

      // Ensure organization and required role exist
      await this.ensureOrganizationExists();
      if (isCompanyUser) {
        await this.ensureAdminRoleExists();
      } else {
        await this.ensureGuestRoleExists();
      }

      // Use email-normalized format for member document ID (consistent with backend)
      const normalizedUserId = this.emailToDocId(userEmail);
      const memberRef = doc(db, 'organizations', this.organizationId, 'members', normalizedUserId);

      const memberData = {
        role: assignedRole,
        joinedAt: serverTimestamp(),
        email: userEmail,
        displayName: userEmail.split('@')[0],
        addedBy: 'auto_add_domain_whitelist',
        uid: normalizedUserId,
        originalUid: userId,
        emailDocId: normalizedUserId
      };

      await setDoc(memberRef, memberData);

      console.log(`✅ User added to organization with ${assignedRole} role (member ID: ${normalizedUserId})`);
      return { success: true };

    } catch (error) {
      console.error('❌ Error adding user to organization:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Ensure organization exists
   */
  async ensureOrganizationExists() {
    try {
      const orgRef = doc(db, 'organizations', this.organizationId);
      const orgSnap = await getDoc(orgRef);

      if (!orgSnap.exists()) {
        console.log('🔄 Creating organization...');

        await setDoc(orgRef, {
          name: 'Drishti Lifesaving',
          description: 'Drishti Lifesaving Organization',
          createdAt: serverTimestamp(),
          createdBy: 'system'
        });

        console.log('✅ Organization created');
      } else {
        console.log('✅ Organization already exists');
      }
    } catch (error) {
      console.error('❌ Error ensuring organization exists:', error);
    }
  }

  /**
   * Ensure guest role exists with proper permissions
   */
  async ensureGuestRoleExists() {
    try {
      const roleRef = doc(db, 'roles', 'guest');
      const roleSnap = await getDoc(roleRef);

      if (!roleSnap.exists()) {
        console.log('🔄 Creating guest role with limited permissions...');

        const guestPermissions = {
          viewTowers: true,
          viewGeofences: ['beach'], // Only beach geofences
          viewOffices: false,
          viewVehicles: false,
          viewInventory: false,
          viewLgCount: false,
          viewAurus: false,
          viewTriton: false,
          editVehicles: false,
          editTowers: false,
          editGeofences: false,
          manageUsers: false,
          viewReports: false,
          adminAccess: false,
          canViewMap: true,
          canViewDevices: false,
          canViewReports: false,
          canManageUsers: false,
          canManageDevices: false,
          canManageGeofences: false,
          canAccessAdminPanel: false
        };

        await setDoc(roleRef, {
          name: 'Guest',
          permissions: guestPermissions,
          description: 'Limited access - can view towers and beach geofences only',
          createdAt: serverTimestamp()
        });

        console.log('✅ Guest role created successfully');
      } else {
        console.log('✅ Guest role already exists');
      }
    } catch (error) {
      console.error('❌ Error ensuring guest role exists:', error);
      // Don't throw - allow function to continue even if role creation fails
    }
  }

  /**
   * Ensure admin role exists with proper permissions
   */
  async ensureAdminRoleExists() {
    try {
      const roleRef = doc(db, 'roles', 'admin');
      const roleSnap = await getDoc(roleRef);

      if (!roleSnap.exists()) {
        console.log('🔄 Creating admin role with full permissions...');

        const adminPermissions = {
          viewVehicles: true,
          viewTowers: true,
          viewOffices: true,
          viewGeofences: ['all'], // All geofences
          viewInventory: true,
          viewLgCount: true,
          viewAurus: true,
          viewTriton: true,
          editVehicles: true,
          editTowers: true,
          editGeofences: true,
          manageUsers: true,
          viewReports: true,
          adminAccess: true
        };

        await setDoc(roleRef, {
          name: 'Administrator',
          description: 'Full access to all features',
          permissions: adminPermissions,
          createdAt: serverTimestamp(),
          createdBy: 'system'
        });

        console.log('✅ Admin role created with full permissions');
      } else {
        console.log('✅ Admin role already exists');
      }
    } catch (error) {
      console.error('❌ Error ensuring admin role exists:', error);
    }
  }

  /**
   * Get default permissions (most restrictive)
   */
  getDefaultPermissions() {
    return {
      viewVehicles: false,
      viewTowers: false,
      viewOffices: false,
      viewGeofences: [],
      viewInventory: false,
      viewLgCount: false,
      viewAurus: false,
      viewTriton: false
    };
  }

  /**
   * Get filtered data based on user permissions
   */
  async getFilteredData(dataType) {
    if (!this.userPermissions) {
      console.warn('⚠️ No permissions loaded');
      return [];
    }

    switch (dataType) {
      case 'vehicles':
        return this.filterVehicles();
      case 'towers':
        return this.userPermissions.viewTowers ? await this.getTowers() : [];
      case 'offices':
        return this.userPermissions.viewOffices ? await this.getOffices() : [];
      case 'geofences':
        return this.userPermissions.viewGeofences ? await this.getGeofences() : [];
      default:
        return [];
    }
  }

  /**
   * Filter vehicles based on permissions
   */
  async filterVehicles() {
    if (this.userPermissions.viewVehicles === true) {
      // Admin access - get all vehicles
      return await this.getAllVehicles();
    } else if (Array.isArray(this.userPermissions.viewVehicles)) {
      // Collection-based access
      const filteredVehicles = [];
      for (const collectionName of this.userPermissions.viewVehicles) {
        const vehicles = await this.getVehiclesFromCollection(collectionName);
        filteredVehicles.push(...vehicles);
      }
      return filteredVehicles;
    }
    return [];
  }

  /**
   * Get vehicles from specific collection
   */
  async getVehiclesFromCollection(collectionName) {
    try {
      const vehiclesRef = collection(db, 'vehicles', collectionName, 'vehicles');
      const q = query(vehiclesRef, where('organizationId', '==', this.organizationId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        collectionName: collectionName
      }));
    } catch (error) {
      console.error(`❌ Error fetching vehicles from ${collectionName}:`, error);
      return [];
    }
  }

  /**
   * Get all vehicles (admin access)
   */
  async getAllVehicles() {
    try {
      const allVehicles = [];
      const vehicleTypes = ['atv', 'ebike', 'office_vehicle', 'operation_vehicle', 'store_vehicles'];

      for (const vehicleType of vehicleTypes) {
        const vehicles = await this.getVehiclesFromCollection(vehicleType);
        allVehicles.push(...vehicles);
      }

      return allVehicles;
    } catch (error) {
      console.error('❌ Error fetching all vehicles:', error);
      return [];
    }
  }

  /**
   * Get towers
   */
  async getTowers() {
    try {
      const towersRef = collection(db, 'towers');
      const q = query(towersRef, where('organizationId', '==', this.organizationId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching towers:', error);
      return [];
    }
  }

  /**
   * Get offices
   */
  async getOffices() {
    try {
      const officesRef = collection(db, 'offices');
      const q = query(officesRef, where('organizationId', '==', this.organizationId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching offices:', error);
      return [];
    }
  }

  /**
   * Get geofences
   */
  async getGeofences() {
    try {
      const geofencesRef = collection(db, 'geofences');
      const q = query(geofencesRef, where('organizationId', '==', this.organizationId));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error fetching geofences:', error);
      return [];
    }
  }

  /**
   * Sign out
   */
  async signOut() {
    try {
      await signOut(auth);
      this.currentUser = null;
      this.userPermissions = null;
      console.log('✅ User signed out');
      return { success: true };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, async (user) => {
      this.currentUser = user;
      if (user) {
        // Don't automatically load permissions - let the auth slice handle it
        // This allows for OTP verification flow to work properly
        this.userPermissions = null;
      } else {
        this.userPermissions = null;
      }
      callback(user, this.userPermissions);
    });
  }

  /**
   * Get current user permissions
   */
  getPermissions() {
    return this.userPermissions;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission) {
    if (!this.userPermissions) return false;
    return this.userPermissions[permission] === true ||
      (Array.isArray(this.userPermissions[permission]) &&
        this.userPermissions[permission].length > 0);
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Load user permissions after OTP verification
   */
  async loadUserPermissionsAfterOTP(userUid, userEmail = null) {
    try {
      console.log('🔄 Loading user permissions after OTP verification...');
      await this.loadUserPermissions(userUid, userEmail);
      return this.userPermissions;
    } catch (error) {
      console.error('❌ Error loading permissions after OTP:', error);
      return null;
    }
  }

  /**
   * Generate and send OTP to user's email using Firestore OTP service
   * @param {Object} user - User object with email and uid
   * @returns {Promise<{success: boolean, otpId?: string, error?: string}>}
   */
  async generateAndSendOTP(user) {
    try {
      console.log('📧 Generating OTP for:', user.email);

      // Use Firestore OTP service
      const result = await firestoreOTPService.requestOTP(
        user.email,
        user.uid,
        user.displayName || user.email.split('@')[0]
      );

      if (result.success) {
        console.log('✅ OTP generated and sent successfully');
        return { success: true, otpId: result.otpId };
      } else {
        console.error('❌ Failed to generate OTP:', result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      console.error('❌ Error generating OTP:', error);
      return { success: false, error: 'Failed to generate OTP' };
    }
  }

  /**
   * Verify OTP entered by user using Firestore OTP service
   * @param {Object} user - User object with email
   * @param {string} otpCode - OTP code entered by user
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async verifyOTP(user, otpCode) {
    try {
      console.log('🔐 Verifying OTP for:', user.email);

      // Use Firestore OTP service
      const result = await firestoreOTPService.verifyOTP(user.email, otpCode);

      if (result.success) {
        console.log('✅ OTP verified successfully');
        return { success: true };
      } else {
        console.error('❌ OTP verification failed:', result.message);
        return { success: false, error: result.message };
      }
    } catch (error) {
      console.error('❌ Error verifying OTP:', error);
      return { success: false, error: 'Failed to verify OTP' };
    }
  }
}

// Create singleton instance
const simplifiedAuthService = new SimplifiedAuthService();

// Export test function for debugging Firebase Auth configuration
export const testFirebaseAuth = async () => {
  console.log('🧪 Testing Firebase Auth Configuration...');
  
  try {
    // 1. Check Firebase config
    console.log('1️⃣ Firebase Config:', {
      apiKey: auth.app.options.apiKey ? '✅ Present' : '❌ Missing',
      authDomain: auth.app.options.authDomain,
      projectId: auth.app.options.projectId
    });
    
    // 2. Check current user
    console.log('2️⃣ Current user:', auth.currentUser?.email || 'None');
    
    // 3. Test provider configuration
    const provider = new GoogleAuthProvider();
    console.log('3️⃣ Google Provider:', provider ? '✅ Created' : '❌ Failed');
    
    // 4. Check if domain is authorized
    const allowedDomains = [
      'localhost',
      '127.0.0.1',
      'dwpl-atlas.firebaseapp.com'
    ];
    const currentDomain = window.location.hostname;
    const currentOrigin = window.location.origin;
    const projectId = auth.app.options.projectId;
    const consoleUrl = `https://console.firebase.google.com/project/${projectId}/authentication/settings`;
    
    const isLocalhostWithPort = currentDomain === 'localhost' && currentOrigin.includes(':');
    const portNumber = isLocalhostWithPort ? currentOrigin.split(':').pop() : null;
    
    console.log('4️⃣ Current domain:', currentDomain);
    console.log('   Current origin:', currentOrigin);
    console.log('   Is in allowed list:', allowedDomains.includes(currentDomain) ? '✅ Yes' : '⚠️ May not be');
    
    if (isLocalhostWithPort) {
      console.log('   ⚠️ IMPORTANT: You are using localhost with port', portNumber);
      console.log('   ⚠️ Firebase may require the FULL origin including port');
      console.log('   📝 Add to Firebase Console:', currentOrigin);
      console.log('   💡 Even though "localhost" is authorized, "localhost:3000" might need to be added separately');
    } else {
      console.log('   ⚠️ Note: Domain must be added in Firebase Console even if listed here');
    }
    
    console.log('   🔗 Fix it here:', consoleUrl);
    console.log('   📝 Add domain:', isLocalhostWithPort ? currentOrigin : currentDomain);
    
    // 5. Test popup capability
    console.log('5️⃣ Testing popup...');
    const testPopup = window.open('about:blank', '_blank', 'width=500,height=600');
    if (testPopup) {
      console.log('   ✅ Pop-ups are allowed');
      testPopup.close();
    } else {
      console.log('   ❌ Pop-ups are BLOCKED');
      console.log('   💡 Please allow pop-ups for this site');
    }
    
    // 6. Check sessionStorage
    const hasRedirectFlag = sessionStorage.getItem('oauth_redirect_started');
    console.log('6️⃣ Redirect flag:', hasRedirectFlag ? '✅ Set' : '❌ Not set');
    
    console.log('✅ Test complete - check results above');
    console.log('💡 To test sign-in, call: simplifiedAuthService.signInWithGoogle()');
    
  } catch (error) {
    console.error('❌ Test failed:', error);
  }
};

// Utility function to manually clear redirect flags (for debugging)
export const clearRedirectFlags = () => {
  sessionStorage.removeItem('oauth_redirect_started');
  sessionStorage.removeItem('oauth_redirect_time');
  console.log('✅ Redirect flags cleared');
  console.log('💡 You can now try signing in again');
};

// Make test function and utilities available globally for console debugging
if (typeof window !== 'undefined') {
  window.testFirebaseAuth = testFirebaseAuth;
  window.simplifiedAuthService = simplifiedAuthService;
  window.clearRedirectFlags = clearRedirectFlags;
  console.log('💡 Debug utilities available:');
  console.log('   - testFirebaseAuth() - Test Firebase Auth configuration');
  console.log('   - clearRedirectFlags() - Clear stuck redirect flags');
  console.log('   - simplifiedAuthService.signInWithGoogle() - Test sign-in');
}

export default simplifiedAuthService;
// Cache buster: 1760329381
